/**
 * Frontend Validation Utility
 * Provides comprehensive validation for form data before submission
 */

class ValidationError extends Error {
  constructor(message, field, value) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
    this.value = value;
  }
}

class FrontendValidator {
  constructor() {
    this.errors = [];
  }

  /**
   * Validates data against a schema
   * @param {Object} data - Data to validate
   * @param {Object} schema - Validation schema
   * @returns {Object} - Validation result with isValid and errors
   */
  validate(data, schema) {
    this.errors = [];
    
    if (!data || typeof data !== 'object') {
      this.errors.push({
        field: 'root',
        message: 'Data must be a valid object',
        value: data
      });
      return { isValid: false, errors: this.errors };
    }

    // Check required fields
    if (schema.required) {
      this.validateRequiredFields(data, schema.required);
    }

    // Check field types and constraints
    if (schema.properties) {
      this.validateProperties(data, schema.properties);
    }

    return {
      isValid: this.errors.length === 0,
      errors: this.errors
    };
  }

  /**
   * Validates required fields
   * @param {Object} data - Data to validate
   * @param {Array} requiredFields - Array of required field names
   */
  validateRequiredFields(data, requiredFields) {
    requiredFields.forEach(field => {
      if (!(field in data) || data[field] === null || data[field] === undefined || data[field] === '') {
        this.errors.push({
          field,
          message: `Field '${field}' is required`,
          value: data[field]
        });
      }
    });
  }

  /**
   * Validates object properties against schema
   * @param {Object} data - Data to validate
   * @param {Object} properties - Schema properties definition
   */
  validateProperties(data, properties) {
    Object.keys(properties).forEach(field => {
      const fieldSchema = properties[field];
      const value = data[field];

      // Skip validation if field is not present and not required
      if (value === undefined || value === null) {
        return;
      }

      // Validate field type
      this.validateFieldType(field, value, fieldSchema);

      // Validate constraints
      this.validateConstraints(field, value, fieldSchema);
    });
  }

  /**
   * Validates field type
   * @param {string} field - Field name
   * @param {*} value - Field value
   * @param {Object} fieldSchema - Field schema definition
   */
  validateFieldType(field, value, fieldSchema) {
    const expectedType = fieldSchema.type;
    
    if (expectedType === 'string' && typeof value !== 'string') {
      this.errors.push({
        field,
        message: `Field '${field}' must be a string`,
        value
      });
    } else if (expectedType === 'number' && typeof value !== 'number') {
      this.errors.push({
        field,
        message: `Field '${field}' must be a number`,
        value
      });
    } else if (expectedType === 'boolean' && typeof value !== 'boolean') {
      this.errors.push({
        field,
        message: `Field '${field}' must be a boolean`,
        value
      });
    } else if (expectedType === 'array' && !Array.isArray(value)) {
      this.errors.push({
        field,
        message: `Field '${field}' must be an array`,
        value
      });
    } else if (expectedType === 'object' && (typeof value !== 'object' || Array.isArray(value))) {
      this.errors.push({
        field,
        message: `Field '${field}' must be an object`,
        value
      });
    } else if (expectedType === 'date' && !this.isValidDate(value)) {
      this.errors.push({
        field,
        message: `Field '${field}' must be a valid date`,
        value
      });
    } else if (expectedType === 'email' && !this.isValidEmail(value)) {
      this.errors.push({
        field,
        message: `Field '${field}' must be a valid email address`,
        value
      });
    } else if (expectedType === 'url' && !this.isValidUrl(value)) {
      this.errors.push({
        field,
        message: `Field '${field}' must be a valid URL`,
        value
      });
    }
  }

  /**
   * Validates field constraints
   * @param {string} field - Field name
   * @param {*} value - Field value
   * @param {Object} fieldSchema - Field schema definition
   */
  validateConstraints(field, value, fieldSchema) {
    // String constraints
    if (fieldSchema.type === 'string') {
      if (fieldSchema.minLength && value.length < fieldSchema.minLength) {
        this.errors.push({
          field,
          message: `Field '${field}' must be at least ${fieldSchema.minLength} characters long`,
          value
        });
      }
      if (fieldSchema.maxLength && value.length > fieldSchema.maxLength) {
        this.errors.push({
          field,
          message: `Field '${field}' must be no more than ${fieldSchema.maxLength} characters long`,
          value
        });
      }
      if (fieldSchema.pattern && !fieldSchema.pattern.test(value)) {
        this.errors.push({
          field,
          message: `Field '${field}' format is invalid`,
          value
        });
      }
    }

    // Number constraints
    if (fieldSchema.type === 'number') {
      if (fieldSchema.min !== undefined && value < fieldSchema.min) {
        this.errors.push({
          field,
          message: `Field '${field}' must be at least ${fieldSchema.min}`,
          value
        });
      }
      if (fieldSchema.max !== undefined && value > fieldSchema.max) {
        this.errors.push({
          field,
          message: `Field '${field}' must be no more than ${fieldSchema.max}`,
          value
        });
      }
    }

    // Array constraints
    if (fieldSchema.type === 'array') {
      if (fieldSchema.minItems && value.length < fieldSchema.minItems) {
        this.errors.push({
          field,
          message: `Field '${field}' must have at least ${fieldSchema.minItems} items`,
          value
        });
      }
      if (fieldSchema.maxItems && value.length > fieldSchema.maxItems) {
        this.errors.push({
          field,
          message: `Field '${field}' must have no more than ${fieldSchema.maxItems} items`,
          value
        });
      }
    }

    // Enum constraints
    if (fieldSchema.enum && !fieldSchema.enum.includes(value)) {
      this.errors.push({
        field,
        message: `Field '${field}' must be one of: ${fieldSchema.enum.join(', ')}`,
        value
      });
    }
  }

  /**
   * Checks if a value is a valid date
   * @param {*} value - Value to check
   * @returns {boolean}
   */
  isValidDate(value) {
    if (value instanceof Date) {
      return !isNaN(value.getTime());
    }
    if (typeof value === 'string') {
      const date = new Date(value);
      return !isNaN(date.getTime());
    }
    return false;
  }

  /**
   * Checks if a value is a valid email
   * @param {string} email - Email to validate
   * @returns {boolean}
   */
  isValidEmail(email) {
    if (typeof email !== 'string') return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Checks if a value is a valid URL
   * @param {string} url - URL to validate
   * @returns {boolean}
   */
  isValidUrl(url) {
    if (typeof url !== 'string') return false;
    try {
      new URL(url);
      return true;
    } catch (_e) {
      return false;
    }
  }

  /**
   * Validates nested object
   * @param {Object} data - Data to validate
   * @param {Object} schema - Schema for nested object
   * @returns {Object} - Validation result
   */
  validateNested(data, schema) {
    return this.validate(data, schema);
  }
}

// Predefined validation schemas for common models
export const ValidationSchemas = {
  Song: {
    type: 'object',
    required: ['title', 'artist'],
    properties: {
      title: { 
        type: 'string', 
        minLength: 1, 
        maxLength: 200 
      },
      artist: { 
        type: 'object',
        required: ['name'],
        properties: {
          name: { 
            type: 'string', 
            minLength: 1, 
            maxLength: 100 
          },
          bio: { 
            type: 'string', 
            maxLength: 1000 
          }
        }
      },
      composer: { 
        type: 'array',
        items: { 
          type: 'string',
          minLength: 1,
          maxLength: 100
        }
      },
      lyric: { 
        type: 'string', 
        maxLength: 10000 
      },
      media: { 
        type: 'array',
        items: {
          type: 'object',
          properties: {
            type: { 
              type: 'string',
              enum: ['audio', 'youtube', 'spotify']
            },
            preview: { type: 'string' }
          }
        }
      },
      tag: { 
        type: 'array',
        items: { 
          type: 'string',
          minLength: 1,
          maxLength: 50
        }
      },
      chord: { 
        type: 'string', 
        maxLength: 1000 
      },
      type: { 
        type: 'string',
        enum: ['SONG', 'INSTRUMENTAL', 'COVER']
      }
    }
  },

  Playlist: {
    type: 'object',
    required: ['name'],
    properties: {
      name: { 
        type: 'string', 
        minLength: 1, 
        maxLength: 100 
      },
      songs: { 
        type: 'array',
        items: { type: 'string' }
      },
      elements: { 
        type: 'array',
        items: { 
          type: 'object',
          properties: {
            type: { 
              type: 'string',
              enum: ['song', 'text', 'image', 'video']
            },
            content: { type: 'string' }
          }
        }
      },
      configuration: { type: 'object' },
      shared: { 
        type: 'array',
        items: {
          type: 'object',
          properties: {
            user: { type: 'string' },
            team: { type: 'string' },
            role: { 
              type: 'string',
              enum: ['viewer', 'editor', 'admin']
            }
          }
        }
      },
      presentation: { 
        type: 'array',
        items: { type: 'string' }
      },
      seen: { type: 'boolean' },
      favoriteToUsers: { 
        type: 'array',
        items: { type: 'string' }
      }
    }
  },

  Artist: {
    type: 'object',
    required: ['name'],
    properties: {
      name: { 
        type: 'string', 
        minLength: 1, 
        maxLength: 100 
      },
      bio: { 
        type: 'string', 
        maxLength: 1000 
      },
      totalLyrics: { 
        type: 'number', 
        min: 0 
      }
    }
  },

  Team: {
    type: 'object',
    required: ['name'],
    properties: {
      name: { 
        type: 'string', 
        minLength: 1, 
        maxLength: 100 
      },
      description: { 
        type: 'string', 
        maxLength: 500 
      },
      members: { 
        type: 'array',
        items: { type: 'string' }
      }
    }
  },

  Event: {
    type: 'object',
    required: ['title', 'date', 'startTime', 'endTime', 'category'],
    properties: {
      title: { 
        type: 'string', 
        minLength: 1, 
        maxLength: 200 
      },
      date: { 
        type: 'date'
      },
      startTime: { 
        type: 'string',
        pattern: /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
      },
      endTime: { 
        type: 'string',
        pattern: /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
      },
      description: { 
        type: 'string', 
        maxLength: 1000 
      },
      category: { 
        type: 'string', 
        maxLength: 200 
      }
    }
  },

  User: {
    type: 'object',
    required: ['name', 'email'],
    properties: {
      name: { 
        type: 'string', 
        minLength: 2, 
        maxLength: 100 
      },
      email: { 
        type: 'email'
      },
      role: { 
        type: 'string',
        enum: ['USER', 'ADMIN', 'MODERATOR']
      },
      age: { 
        type: 'number', 
        min: 18, 
        max: 120 
      }
    }
  }
};

// Create singleton instance
const validator = new FrontendValidator();

export default validator;
export { ValidationError, FrontendValidator };
